const state = {
  modalContent: {},
  pendingItems: {},
  showUpsell: false,
}

const root = { root: true };

const actions = {
  /**
   * @desc Fetch the upsell product and add it to products store if its not already there.
   *     Shape the payload to include the fetched model and set it as the modalContent.
   * @param {*} context 
   * @param {Object} payload - The upsell model, defined either as a global or product-level fieldset. Both fieldset types have identical keys.
   * @param {String} payload.product - The handle of the product to be upsold. 
   * @param {Number} payload.disable_upsell - Either 0 or 1, used to determine if the upsell should be disabled.
   * @param {String} payload.headline - The headline for the modal.
   * @param {String} payload.short_description - Short description for the modal.
   * @param {String} payload.add_to_cart_button_text - Text to be displayed within the add to cart button.
   * @param {String} payload.close_upsell_button_Text - Text to be displayed within the button to dismiss the modal.
   */
  setUpsell: async ({ commit, rootState }, payload) => {
    if (payload.disable_upsell) {
      commit('SET_UPSELL', payload);
      return;
    }

    const req = await fetch(`/products/${payload.product}?view=json`);
    const model = await req.json();
    const shapedPayload = { ...payload, product: model };

    if (!rootState.products.all.find(({ handle }) => handle === payload.product)) {
      commit('products/ADD_PRODUCT', model, root);
    }

    commit('SET_UPSELL', shapedPayload);
  },
  /**
   * @desc Determine which upsell should be shown, if any.
   *     CRITERIA
   *       1. If the product being upsold is already in user's cart, don't display.
   *       2. If a global upsell is declined, don't show it again during the session.
   *       3. If a scoped upsell is declined and the user views a unique PDP during
   *          their session that has a separate product being upsold, show it regardless
   *          of whether or not they accepted the earlier upsell.
   *       4. Lastly, if there was no upsell identified or the upsell is disabled on a
   *          product level, give control back to the product form.
   * 
   * @param {Object[]} items - Array of items the user is attempting to add to their cart.
   * @returns {Boolean} proceed - Whether or not the dispatching function should continue
   *     with the add to cart process or leave the rest of the work to the upsell.
   */
  cartAddEvent: async ({ commit, state, rootState }, items) => {
    const { modalContent } = state;
    if (modalContent.disable_upsell || !Object.keys(modalContent).length) return true;

    try {
      const { id, selected_or_first_available_variant: variant } = modalContent.product;
  
      // User already has the upsold item in their cart.
      const upsellInCart = rootState.cart.checkout.items.find(({ id: lineItemId }) => lineItemId === variant.id);
      // User has already been presented with this upsell.
      const currentUpsell = sessionStorage.getItem(`upsell_${id}`);
      // The upsold item is the item being added to cart.
      const upsellIsItem = items.find(({ id: itemAddedId }) => itemAddedId === variant.id);
      // The upsell is disabled for this product.
      const upsellDisabled = modalContent.disable_upsell;
      // selected or first available variant is out of stock.
      const noStock = variant.available === false;
  
      if (upsellInCart || currentUpsell || upsellIsItem || upsellDisabled || noStock) {
        return true;
      }
  
      commit('SET_VISIBILITY', true);
      sessionStorage.setItem(`upsell_${id}`, modalContent.product.id);
      return false;
    } catch (e) {
      console.error('Could not process upsell information in cartAddEvent.')
      console.error(e);
      return true;
    }
  },

  /**
   * 
   * @param {*} context
   * @param {Object[]} payload - An array of items that should be added to cart when the user initiates an upsell add or decline.
   * @param {Number} payload.id - ID of the variant to be added to the user's cart.
   * @param {Number} payload.quantity - Quantiy of the variant to be added to the user's cart.
   */
  pendingAddToCart: ({ commit }, payload) => {
    commit('SET_PENDING_ITEMS', payload);
  },

  addedUpsell: ({ dispatch, state }) => {
    const { selected_or_first_available_variant: upsellVariant } = state.modalContent.product;
    const { pendingItems } = state;
    const { id } = state.modalContent.product;

    sessionStorage.setItem(`upsell_${id}`, true);

    pendingItems.push({
      id: upsellVariant.id,
      quantity: 1,
    });

    dispatch('closeUpsell', false);

    dispatch('cart/addItems', pendingItems, root)
      .then(() => { dispatch('cart/afterAddToCart', null, root) })
      .catch((error) => {
        return dispatch('toast/send', { text: error, type: 'error' }, root)
      });
  },

  /**
   * @desc Close the modal and assign responsibility for adding the pending items to the
   *     user's cart. If this action is responsible for adding the pending items to the cart,
   *     then the user has declined the upsell product and this action will set the
   *     sessionStorage item indicating the upsold product was declined.
   * @param {*} context 
   * @param {Boolean} addPending - A Boolean indicating whether or not the closeUpsell
   *     action is responsible for adding the pendingItems to the user's cart.
   */
  closeUpsell: ({ commit, dispatch, state }, addPending = true) => {
    const { pendingItems } = state;
    const { id } = state.modalContent.product;

    commit('SET_VISIBILITY', false);
    if (!addPending) return;

    sessionStorage.setItem(`upsell_${id}`, false);

    if (pendingItems) {
      dispatch('cart/addItems', pendingItems, root)
        .then(() => {
          dispatch('cart/afterAddToCart', null, root);
        })
        .catch(error => {
          dispatch('toast/send', { text: error, type: 'error' }, root);
        });
    }
  },
}

const mutations = {
  SET_UPSELL: (state, payload) => {
    state.modalContent = payload;
  },
  SET_PENDING_ITEMS: (state, payload) => {
    state.pendingItems = payload;
  },
  SET_VISIBILITY: (state, payload) => {
    state.showUpsell = payload;
  }
}

export default { namespaced: true, state, mutations, actions }